'use client';

/*
  This first post was originally not written in MDX.

  Rather than spend a bunch of time converting it, I'm just "wrapping" it, by
  importing the original component.

  Not the cleanest solution, but by far the fastest :)
*/
/* eslint-disable */
import dynamic from 'next/dynamic';

import { getInterpolatedValue } from '@/utils';

import Paragraph from '@/components/Paragraph';
import Aside from '@/components/Aside';
import List from '@/components/List';
import {
  SectionHeading,
  SectionSubHeading,
} from '@/components/ContentHeading';
import InlineCode from '@/components/InlineCode';
import TextLink from '@/components/Link/TextLink';
import Divider from '@/components/Divider';
import Em from '@/components/Spicy';
import Spacer from '@/components/Spacer';
import Playground from '@/components/Playground';
import VideoGif from '@/components/VideoGif';

import BezierController from './BezierController';
import BezierFlattener from './BezierFlattener';
import BezierInterpolationGraph from './BezierInterpolationGraph';
import InitialCurveDemo from './InitialCurveDemo';

const CodeSnippet = dynamic(
  () => import('@/components/CodeSnippet/ClientOnlySnippet'),
  { ssr: false }
);

function MainContent() {
  return (
    <>
      <Paragraph>
        In 2018, I had a problem. I had been publishing my blog posts
        on Medium, but I was getting frustrated by the lack of
        control. I had all these whimsical ideas bouncing around in my
        head, and no outlet! And so, I decided to build my own blog.
      </Paragraph>

      <Paragraph>
        Looking back, the first version of my blog was pretty cringy,
        but there was one cool feature that I'm still pretty happy
        with. The lime-green hero had these dramatic swoops, and they
        flattened out on scroll:
      </Paragraph>

      <VideoGif
        src="/videos/dynamic-bezier-curves/original-blog-hero.mp4"
        width={1362 / 2}
        height={710 / 2}
      />

      <Paragraph>
        In this blog post, I'm going to show you how I did this. We’ll
        learn how to create swoopy SVGs that can be updated
        dynamically:
      </Paragraph>

      <InitialCurveDemo />

      <Aside type="warning" title="Blast from the past">
        <Paragraph>
          This post was originally published in 2018, and honestly,
          it's far from my best work 😅. In 2024, I migrated it to my
          new blog, fixing some of the technical issues, but without
          revising the content.
        </Paragraph>
        <Paragraph>
          In terms of whether the code is outdated: the SVG stuff is
          still relevant. The React code, however, uses the older
          “class component” syntax, and should be updated before use.
        </Paragraph>
      </Aside>

      <SectionHeading>A Quick SVG Refresher</SectionHeading>
      <Paragraph>
        For achieving this effect, we'll use SVG. We could also use
        HTML Canvas, but I generally prefer to work with SVG. It's
        more React-like in its API, there's less complexity in setting
        it up, and it's more a11y-friendly.
      </Paragraph>
      <Paragraph>
        While doing a deep dive into SVG is beyond the scope of this
        post (I'd recommend the{' '}
        <TextLink href="https://www.w3schools.com/graphics/svg_intro.asp">
          W3Schools tutorial
        </TextLink>{' '}
        for that), we'll cover the basics, and show how to create some
        shapes from scratch. Experienced SVG-ers can jump to{' '}
        <TextLink href="#intro-to-bezier-curves">
          the next section
        </TextLink>
        .
      </Paragraph>
      <Paragraph>
        The simplest form of SVG drawings use shape elements, like{' '}
        <InlineCode>{'<rect>'}</InlineCode> or{' '}
        <InlineCode>{'<ellipse>'}</InlineCode>.
      </Paragraph>

      <Paragraph>
        Try tweaking some of the values below, to build an
        understanding of how SVG shapes work:
      </Paragraph>

      <Playground
        id="basic-shapes"
        html={basicShapesCode}
        layoutMode="tabbed"
        centered
      />

      <Paragraph>
        These shapes are straightforward and declarative, but that
        simplicity comes at the cost of flexibility; you can only
        create a handful of different shapes.
      </Paragraph>
      <Paragraph>
        To do neat curvy things, we need to use the{' '}
        <InlineCode>{'<path>'}</InlineCode> element. This
        swiss-army-knife of an SVG primitive lets you specify a
        sequence of steps to execute, in a seemingly-inscrutable
        bundle of letters and numbers:
      </Paragraph>

      <Playground
        id="intro-path"
        html={pathIntroCode}
        layoutMode="tabbed"
        centered
      />

      <Paragraph>
        The interactive code snippet above uses 2 commands:
      </Paragraph>
      <List>
        <List.ListItem>
          <InlineCode>M</InlineCode>, which instructs the path to{' '}
          <Em>move</Em> to a specific coordinate.
        </List.ListItem>
        <List.ListItem>
          <InlineCode>L</InlineCode>, which instructs the path to
          create a <Em>line</Em> from the current position to the
          specified coordinate.
        </List.ListItem>
      </List>
      <Paragraph>
        After the commands <InlineCode>M</InlineCode> and{' '}
        <InlineCode>L</InlineCode>, we see some numbers. These can be
        thought of as "arguments" for the commands. In this case, the
        arguments are coordinates; both commands require a single X/Y
        pair.
      </Paragraph>
      <Paragraph>
        In other words, we can read the above path as: "Move to{' '}
        <InlineCode>{'{x: 100, y: 100}'}</InlineCode>, then draw a
        line to <InlineCode>{'{x: 200, y: 100}'}</InlineCode>", and so
        on.
      </Paragraph>
      <Paragraph>
        The coordinate system is relative to the values specified in
        the <InlineCode>viewBox</InlineCode>. The current viewbox
        specifies that the viewable area has a top-left corner of 0/0,
        a width of 300, and a height of 300. So all of the coordinates
        specified in the <InlineCode>path</InlineCode> are within that
        300x300 box.
      </Paragraph>
      <Paragraph>
        The <InlineCode>viewBox</InlineCode> is what makes SVGs
        scalable; we can make our SVG any size we like, and everything
        will scale naturally, since the elements within our SVG are
        relative to this 300x300 box.
      </Paragraph>
      <Paragraph>
        The <InlineCode>path</InlineCode> element features{' '}
        <TextLink href="https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths">
          quite a number
        </TextLink>{' '}
        of these commands. There are two that are relevant for our
        purposes:
      </Paragraph>
      <List>
        <List.ListItem>
          <InlineCode>Q</InlineCode>, which instructs the path to
          create a <Em>quadratic</Em> Bézier curve.
        </List.ListItem>
        <List.ListItem>
          <InlineCode>C</InlineCode>, which instructs the path to
          create a <Em>cubic</Em> Bézier curve.
        </List.ListItem>
      </List>
      <Spacer size={80} />
      <SectionHeading>Intro to Bézier Curves</SectionHeading>
      <Paragraph>
        Bézier curves are surprisingly common. Due to their
        versatility, they're a staple in most graphics software like
        Photoshop, but they're also used as timing functions: if
        you've ever used non-linear CSS transitions (like the default
        "ease"), you've already worked with Bézier curves!
      </Paragraph>
      <Paragraph>But what are they, and how do they work?</Paragraph>
      <Paragraph>
        A Bézier curve is essentially a line from a{' '}
        <Em>start point</Em> to an <Em>end point</Em> that is acted
        upon by one or more <Em>control points</Em>. A control point
        curves the line towards it, as if the control point was
        pulling it in its direction.
      </Paragraph>
      <Paragraph>
        The following line looks like a straight line, but check out
        what happens when you move the points around—try dragging the
        middle control point up and down.
      </Paragraph>
      <BezierController id="initial" initialType="quadratic" />
      <Paragraph>
        The line above is a <Em>quadratic</Em> Bézier curve; this
        means that it has a <strong>single control point</strong>. I'm
        guessing it gets its name from the fact that you can create
        parabola-like shapes with it:
      </Paragraph>
      <BezierController
        id="parabola"
        initialType="quadratic"
        p1={[400, 15]}
        p2={[500, 395]}
        p4={[600, 15]}
      />
      <Paragraph>
        A <Em>cubic</Em> Bézier curve, in contrast, has{' '}
        <strong>two</strong> control points. This allows for much more
        interesting curves:
      </Paragraph>
      <BezierController
        id="toggleable"
        allowToggle
        initialType="cubic"
        p1={[25, 25]}
        p2={[333, 375]}
        p3={[666, 25]}
        p4={[975, 375]}
      />
      <Paragraph>
        The syntax for Bézier curves in SVG{' '}
        <InlineCode>path</InlineCode> definitions is a little
        counter-intuitive, but it looks like this. The following code
        is written in JSX, rather than HTML, so that I can name the
        variables:
      </Paragraph>
      <CodeSnippet
        includeContentStyles
        code={bezierPathCode}
        lang="jsx"
      />

      <Paragraph>
        The thing that makes this counter-intuitive, to me at least,
        is that the <InlineCode>startPoint</InlineCode> is inferred in
        the <InlineCode>Q</InlineCode> command; while there are 3
        points needed for a quadratic Bézier curve, only 2 points are
        passed as arguments to <InlineCode>Q</InlineCode>.
      </Paragraph>
      <Paragraph>
        Similarly, for a cubic Bézier curve, only the control points
        and the end point are provided to the{' '}
        <InlineCode>C</InlineCode> command.
      </Paragraph>
      <Paragraph>
        This syntax does mean that curves can conveniently be chained
        together, as one curve starts where the last one ends:
      </Paragraph>

      <Playground
        id="chained-curves"
        html={chainedCurvesCode}
        layoutMode="tabbed"
        centered
      />

      <Paragraph>
        Ok, I think that's enough playing with vanilla SVGs. Let's see
        how we can leverage React to make these curves dynamic!
      </Paragraph>

      <SectionHeading>Bézier Curves in React</SectionHeading>
      <Paragraph>
        Up to this point, we've been looking at static SVGs. How do we
        make them change, over time or based on user input?
      </Paragraph>
      <Paragraph>
        Well, in keeping with the "meta" theme of this blog post, why
        not examine the draggable-with-lines Bézier curves from
        earlier in this post?
      </Paragraph>
      <Paragraph>
        There's a fair bit of code to manage this, even in this
        slightly-simplified snippet. I've annotated it heavily, which
        hopefully makes things easier to parse. 🤞
      </Paragraph>

      <CodeSnippet
        includeContentStyles
        lang="jsx"
        code={reactBezierCode}
      />

      <Paragraph>To summarize how this works:</Paragraph>
      <List>
        <List.ListItem>
          React holds variables in component state for{' '}
          <InlineCode>startPoint</InlineCode>,{' '}
          <InlineCode>controlPoint</InlineCode>, and{' '}
          <InlineCode>endPoint</InlineCode>.
        </List.ListItem>
        <List.ListItem>
          In the <Em>render</Em> method, we build the instructions for
          the <InlineCode>path</InlineCode> using these state
          variables.
        </List.ListItem>
        <List.ListItem>
          When the user clicks or taps on one of the points, we update
          the state to keep track of which point is moving with{' '}
          <InlineCode>draggingPointId</InlineCode>.
        </List.ListItem>
        <List.ListItem>
          As the user moves the mouse (or finger) across the SVG's
          surface, we do some calculations to figure out where the
          currently-dragging point needs to move to. This is made
          complex by the fact that SVGs have their own internal
          coordinate system (viewBox), and so we have to translate the
          on-screen pixels to this system.
        </List.ListItem>

        <List.ListItem>
          Once we have the new X/Y coordinate for the active point,{' '}
          <InlineCode>setState</InlineCode> lets React know about this
          state change, and the component re-renders, which causes the{' '}
          <InlineCode>path</InlineCode> to be re-calculated.
        </List.ListItem>
      </List>
      <Spacer size={25} />
      <SectionHeading>A note on performance</SectionHeading>
      <Paragraph>
        By using React's update cycle to manage the point coordinates,
        there is added overhead of letting React run its
        reconciliation cycle on every{' '}
        <InlineCode>mousemove</InlineCode>. Is this prohibitively
        expensive?
      </Paragraph>
      <Paragraph>
        The answer is that it depends. React's reconciliation can be
        surprisingly fast, especially when dealing with such a small
        tree (after all, the only thing that needs to be diffed is an
        SVG). Especially in "production" mode, when React doesn't have
        to do a lot of dev warning checks, this process can take
        fractions of a millisecond.
      </Paragraph>
      <Paragraph>
        I wrote an{' '}
        <TextLink href="https://github.com/joshwcomeau/blog/blob/master/src/pages/posts/dynamic-bezier-curves/code/optimized-react-bezier.example">
          alternative implementation
        </TextLink>{' '}
        that updates the DOM directly. It does run faster (about 50%
        faster in my quick test), but both implementations still clock
        in under 1ms on modern high-end hardware. On the cheapest
        Chromebook I could find, the "unoptimized" one still averaged
        50fps or so.
      </Paragraph>
      <Spacer size={80} />
      <SectionHeading>Curve Interpolation</SectionHeading>
      <Paragraph>
        I seem to have gotten a little side-tracked! Our original goal
        was to create a Bézier curve that flattens itself on scroll.
      </Paragraph>
      <Paragraph>
        Given what we've gone over so far, we have almost all of the
        tools we need to solve this problem! A Bézier curve with its
        control point(s) directly between the start and end points is
        actually a straight line! So we need to transition the control
        points from their curvy values to a flat value.
      </Paragraph>
      <BezierFlattener />
      <Spacer size={32} />
      <Paragraph>
        We need a way to <Em>interpolate values</Em>. We know where
        the control points should be at 0% and 100%, but what about
        when the user is 25% scrolled through the content?
      </Paragraph>
      <Paragraph>
        While we could be fancy and ease the transition, a linear
        transformation works just fine for our purposes. So when the
        user is 50% scrolled through the content, the control points
        will be 50% of the way between their initial curvy value, and
        the flat-line value.
      </Paragraph>
      <Paragraph>
        For this, some secondary-school maths will come in handy. If
        you're already up to speed on interpolation, you can{' '}
        <TextLink href="#handling-scroll-in-react">
          skip this bit
        </TextLink>
        .
      </Paragraph>
      <Paragraph>
        If you plumb the depths of your memory, you may remember how
        to calculate the <Em>slope</Em> of a line. The slope tells you
        how the line changes over time. We calculate it by dividing
        the <strong>change in y</strong> over the{' '}
        <strong>change in x</strong>:
      </Paragraph>
      <Paragraph style={{ textAlign: 'center' }}>
        <InlineCode>slope</InlineCode> ={' '}
        <InlineCode>(y2 - y1) / (x2 - x1)</InlineCode> ={' '}
        <InlineCode>(Δy) / (Δx)</InlineCode>
      </Paragraph>
      <Paragraph>
        There's also this rascal, the linear equation formula. This
        allows us to graph a straight line, and figure out the y value
        for a given x value. By convention, slope is given the
        variable a:
      </Paragraph>
      <Paragraph style={{ textAlign: 'center' }}>
        <InlineCode>y = ax + b</InlineCode>
      </Paragraph>
      <Paragraph>
        How does this relate to interpolation? Well, let's imagine
        that our Bézier curve's control point, when it's all curvy, is
        200 pixels away from its flattened position, so we'll give it
        an initial y value of 200. The x in this case is really a
        measure of progress, so we'll have it range from 0 (completely
        curvy) to 1 (completely flat). If we graph this line, we get
        this:
      </Paragraph>
      <BezierInterpolationGraph />

      <Paragraph>
        To clarify, this line represents the range of possible y
        values for a quadratic Bézier curve's control point. Our x
        values represent the degree of "flattening"; this is useful to
        us because we want to be able to provide an x value like 0.46,
        and figure out what the corresponding y value is (our x value
        will come from user input, like the percentage scrolled
        through the viewport).
      </Paragraph>

      <Paragraph>
        To make our formula work, we need to know at least 2 points on
        this line. Thankfully, we do! We know that the initial
        position, fully curved, is at{' '}
        <InlineCode>{`{ x: 0, y: 200 }`}</InlineCode>, and we know
        that the curve becomes fully flattened at{' '}
        <InlineCode>{`{ x: 1, y: 0 }`}</InlineCode>.
      </Paragraph>
      <List>
        <List.ListItem>
          The slope would be equal to{' '}
          <InlineCode>(Δy) / (Δx)</InlineCode> ={' '}
          <InlineCode>(0 - 200) / (1 - 0)</InlineCode> ={' '}
          <InlineCode>-200 / 1</InlineCode> ={' '}
          <InlineCode>-200</InlineCode>.
        </List.ListItem>
        <List.ListItem>
          Our b value is the y-axis intercept, which is our initial
          curved value, 200.
        </List.ListItem>
        <List.ListItem>
          x will be the ratio of scroll-through, between 0 and 1, that
          we'll get from our scroll handler.
        </List.ListItem>
      </List>
      <Paragraph>Filling it in:</Paragraph>
      <Paragraph style={{ textAlign: 'center' }}>
        <InlineCode>y = -200x + 200</InlineCode>
      </Paragraph>
      <Paragraph>
        If it's 25% of the way through, x will be 0.25, and so our y
        value would be y = (-200)(0.25) + 200 = 150, which is correct:
        150 is 1/4 of the way between 200 and 0.
      </Paragraph>
      <Paragraph>
        Here's our function that performs the above calculations:
      </Paragraph>

      <CodeSnippet
        includeContentStyles
        lang="js"
        code={interpolateFunctionCode}
      />

      <Paragraph>
        Looks like teenage-me was wrong; algebra <strong>is</strong>{' '}
        useful and practical!
      </Paragraph>
      <Spacer size={80} />
      <SectionHeading>Handling Scroll in React</SectionHeading>
      <Paragraph>
        We're in the home stretch now! Time to combine all these ideas
        into something usable.
      </Paragraph>
      <Paragraph>
        Let's start by building a component that contains our
        scroll-handler to interpolate from the bottom of the viewport
        to the top, and connect those values to a Bézier curve in the{' '}
        <Em>render</Em> function:
      </Paragraph>
      <CodeSnippet
        includeContentStyles
        lang="jsx"
        code={reactScrollFlattenerCode}
      />
      <Paragraph>
        This initial approach seems to work OK! There are two things I
        want to improve though:
      </Paragraph>
      <List>
        <List.ListItem>
          The "timing" of the flattening feels wrong to me.
          <br />
          <Spacer size={10} />
          When the curve fully enters the viewport, it's already
          starting to be flattened. We don't get to see it in
          100%-curved form. Worse, it hasn't finished flattening by
          the time it scrolls out of view! This is because this page
          has a header that takes up the top 50px of the viewport, and
          we aren't taking that into account.
          <br />
          <Spacer size={10} />
          To solve these problems, we need to define a{' '}
          <Em>scrollable area</Em>, instead of using the viewport.
        </List.ListItem>
        <List.ListItem>
          This component is doing an awful lot. It feels like we could
          extract a couple components from this. Refactoring it would
          not only make it easier to follow/understand, but it would
          make it more reusable.
        </List.ListItem>
      </List>
      <Paragraph>
        Let's fix these problems. Here's a refactored version:
      </Paragraph>
      <CodeSnippet
        includeContentStyles
        lang="jsx"
        code={reactScrollFlattenerRefactoredCode}
      />
      <Paragraph>
        Ahh, much nicer! The effect is more pleasant as the flattening
        animation happens within a smaller scroll window, and the code
        is easier to parse. As a bonus, our{' '}
        <InlineCode>BezierCurve</InlineCode> and{' '}
        <InlineCode>ScrollArea</InlineCode> components are generic, so
        they could be useful in totally different contexts.
      </Paragraph>
      <Spacer size={25} />
      <SectionHeading>Another note on performance</SectionHeading>
      <Paragraph>
        The two versions above were written without any concern for
        performance. As it turns out, the performance is not so bad;
        on my low-end Chromebook, it stutters a little bit from time
        to time but mostly runs at 60fps. On my sluggish iPhone 6, it
        runs well enough (the biggest issue on mobile is that the
        browser address bar changes on scroll. Because of that, it may
        be wise to disable scroll-based things like this altogether on
        mobile).
      </Paragraph>
      <Paragraph>
        That said, your mileage may vary. If you want to improve
        performance, there are a few ways this could be optimized:
      </Paragraph>
      <List>
        <List.ListItem>
          <TextLink href="https://codeburst.io/throttling-and-debouncing-in-javascript-b01cad5c8edf">
            Throttle
          </TextLink>{' '}
          the scroll-handler in <InlineCode>ScrollArea</InlineCode>{' '}
          that it only fires every 20ms or so. This is to calm down
          certain touch-screen or trackpad interfaces that can fire
          far more often than is required.
        </List.ListItem>
        <List.ListItem>
          One of the more expensive parts of this effect is that we're
          interacting with the DOM, via{' '}
          <TextLink href="https://developer.mozilla.org/en-US/docs/Web/API/Element/getBoundingClientRect">
            <InlineCode>getBoundingClientRect</InlineCode>
          </TextLink>
          , on every scroll event. Ideally, we could cache the
          position of our <InlineCode>ScrollArea</InlineCode> on
          mount, and then check the current scroll distance against
          this value.
          <br />
          <Spacer size={10} />
          Unfortunately, this method opens up new problems. It assumes
          that nothing between the top of the document and your Bézier
          curve will change height, since our calculations assume a
          static distance between the two. Mobile browsers like iOS
          Safari will hide their chrome as you scroll down, so we'd
          have to factor that in as well.
          <br />
          <Spacer size={10} />
          It's far from impossible, but it wasn't worth the trouble
          for me, given that performance was satisfactory on the
          devices I'm targeting.
        </List.ListItem>
        <List.ListItem>
          By storing
          <InlineCode>scrollRatio</InlineCode> in state and
          re-rendering whenever it changes, React needs some time to
          work out how the DOM has changed as a result of the scroll.
          <br />
          <Spacer size={10} />
          The refactor to extract several components, while very good
          for DX and reusability, also means that React has a slightly
          more complex tree to reconcile.
          <br />
          <Spacer size={10} />
          This all sounds a bit scary, but as we discovered earlier,
          React's reconciliation process is very quick on small trees
          like this. The cost of the refactor was negligible on my
          chromebook.
          <br />
          <Spacer size={10} />
          If you really need to extract every drop of performance, you
          could work with the DOM directly, by setting the new{' '}
          <InlineCode>path</InlineCode> instructions using{' '}
          <InlineCode>setAttribute</InlineCode>. Note that you'd need
          to store everything in 1 component again.
        </List.ListItem>
      </List>
      <Divider />
      <SectionHeading>In Conclusion</SectionHeading>
      <Paragraph>
        Whew, you made it through this Bézier deep-dive!
      </Paragraph>
      <Paragraph>
        The technique described in this blog post is foundational, and
        there's tons of flourishes you can add on top of it:
      </Paragraph>
      <List>
        <List.ListItem>
          This blog uses 3 layered Bézier curves with different fill
          colours to provide depth to the experience.
        </List.ListItem>
        <List.ListItem>
          You can experiment with different easings for the
          interpolation (Bézier curves are often used for{' '}
          <TextLink href="http://cubic-bezier.com/">
            timing functions
          </TextLink>
          , after all!). What if the curve got{' '}
          <em>even more dramatic</em> before smoothing it out?
        </List.ListItem>
        <List.ListItem>
          You could experiment with{' '}
          <TextLink href="/animation/a-friendly-introduction-to-spring-physics">
            spring physics
          </TextLink>
          , to give the transition inertia.
        </List.ListItem>
      </List>
      <Paragraph>
        I'm excited to see what you build with this technique! Let me
        know{' '}
        <TextLink href="https://bsky.app/profile/joshwcomeau.com">
          on Bluesky
        </TextLink>
        .
      </Paragraph>

      <Spacer size={80} />
      <SectionHeading>Additional Reading</SectionHeading>
      <Paragraph>
        Learn more about the math and mechanics behind Bézier curves
        with these two amazing resources:
      </Paragraph>
      <List>
        <List.ListItem>
          <TextLink href="http://jamie-wong.com/post/bezier-curves/">
            Bezier Curves from the Ground Up
          </TextLink>
          , by Jamie Wong
        </List.ListItem>
        <List.ListItem>
          <TextLink href="https://pomax.github.io/bezierinfo/">
            A Primer on Bézier curves
          </TextLink>
          , by Mike "Pomax" Kamermans
        </List.ListItem>
      </List>
    </>
  );
}

export default MainContent;

const basicShapesCode = `\
<svg
  width="240"
  viewBox="0 0 80 80"
  style="background: #333;"
>
  <rect
    x="40" y="15"
    width="30" height="65"
    fill="hotpink"
  />
  <ellipse
    cx="30" cy="60"
    rx="20" ry="20"
    fill="lightsalmon"
  />
  <polygon
    points="15,80 30,55 45,80"
    fill="turquoise"
  />
</svg>
`;

const pathIntroCode = `
<svg viewBox="0 0 300 300" fill="none">
  <path
    d="
      M 100,100
      L 200,100
      L 200,200
      L 100,200
      L 100,100
    "
    stroke="hotpink"
    stroke-width="5"
  />
</svg>
`;

const bezierPathCode = `const startPoint = [25, 25];
const controlPoint = [300, 175];
const endPoint = [25, 325];

<svg viewBox="0 0 200 350">
  <path
    d={\`
      M \${startPoint}
      Q \${controlPoint} \${endPoint}
    \`}
    fill="none"
    stroke="hotpink"
    strokeWidth={5}
  />
</svg>`;

const chainedCurvesCode = `\
<svg
  viewBox="0 0 200 200"
  width="200"
>
  <path
    d="
      M 25,25
      C 100,50 25,75 25,100
      C 25,125 300,150 25,175
    "
    fill="none"
    stroke="hotpink"
    stroke-width="5"
  />
</svg>
`;

const reactBezierCode = `class Bezier extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      // These are our 3 Bézier points, stored in state.
      startPoint: { x: 10, y: 10 },
      controlPoint: { x: 190, y: 100 },
      endPoint: { x: 10, y: 190 },

      // We keep track of which point is currently being
      // dragged. By default, no point is.
      draggingPointId: null,
    };
  }

  handleMouseDown(pointId) {
    this.setState({ draggingPointId: pointId });
  }

  handleMouseUp() {
    this.setState({ draggingPointId: null });
  }

  handleMouseMove({ clientX, clientY }) {
    const { viewBoxWidth, viewBoxHeight } = this.props;
    const { draggingPointId } = this.state;

    // If we're not currently dragging a point, this is
    // a no-op. Nothing needs to be done.
    if (!draggingPointId) {
      return;
    }

    // During render, we capture a reference to the SVG
    // we're drawing, and store it on the instance with
    // \`this.node\`.
    // If we were to \`console.log(this.node)\`, we'd see a
    // reference to the underlying HTML element.
    // eg. \`<svg viewBox="0 0 250 250"
    const svgRect = this.node.getBoundingClientRect();

    /*
    Ok, this next bit requires some explanation.

    The SVG rect gives us the element's position relative
    to the viewport.

    The user's mouse position with \`clientX\` and \`clientY\`
    is also relative to the viewport.

    What we actually care about, though, is the cursor's
    position relative to the SVG itself.

    Let's use a diagram! Imagine if X is the user's cursor:


    ------------------------------------------------------
    | viewport            ______________                 |
    |                    |              |                |
    |                    |       X      | <- SVG         |
    |                    |______________|                |
    |____________________________________________________|

    ^----------------------------^ This is the \`clientX\`;
                                   the distance between the
                                   viewport and the cursor.

    ^-------------------^          This is the \`svgRect\`
                                   \`left\` value. Distance
                                   between the viewport and
                                   the SVG's left edge.

                        ^--------^ This is the distance we
                                   care about; the distance
                                   between the SVG's left
                                   edge, and the cursor.

    We can get that value with subtraction!
    */
    const svgX = clientX - svgRect.left;
    const svgY = clientY - svgRect.top;

    /*
    The next problem is that our SVG has a different
    coordinate system: Our SVG's \`viewBox\` might be 250x250,
    while in terms of the screen real-estate it might
    actually take up 500x500 pixels!

    To solve for this, I used cross-multiplication. Here are
    the variables we need:

    - svgX            The value we just calculated. The
                      cursor's \`x\` position within the SVG.

    - viewBoxWidth    The width of the SVG's internal
                      coordinate system. Specified via
                      props to this component.

    - svgRect.width   The on-screen width of the DOM element
                      Returned from \`getBoundingClientRect\`.

    Armed with that data, we can cross-multiply as follows:

         svgX               viewBoxX (unknown)
    --------------    =    --------------------
     viewBoxWidth             svgRect.width

    The left side of this equation is in terms of the screen
    real-estate: our cursor might be 250px into a 500px-wide
    svg.

    The right side is the SVG's viewBox coordinate system.
    We're \`X\` pixels into a 250px-wide viewBox.

    When we re-arrange the formula to solve for \`viewBoxX\`,
    we wind up with:
    */
    const viewBoxX = svgX * viewBoxWidth / svgRect.width;

    // We do the same thing for the vertical direction:
    const viewBoxY = svgY * viewBoxHeight / svgRect.height;

    // Phew! That was a lot of stuff, but in the end we
    // wind up with the user's mouse position within the
    // SVG's viewBox, and can update React state so that it
    // re-renders in this new position!
    this.setState({
      [draggingPointId]: { x: viewBoxX, y: viewBoxY },
    });
  }

  render() {
    const { viewBoxWidth, viewBoxHeight } = this.props;
    const {
      startPoint,
      controlPoint,
      endPoint,
    } = this.state;

    // As we've seen before, the quadratic Bézier curve
    // involves moving to the starting point, and then
    // specifying the control and end points with \`Q\`
    const instructions = \`
      M \${startPoint.x},\${startPoint.y}
      Q \${controlPoint.x},\${controlPoint.y}
        \${endPoint.x},\${endPoint.y}
    \`;

    // While the Bézier curve is the main attraction,
    // we also have several shapes, including:
    //   - the handles for the start/control/end points
    //   - the dashed line that shows how the control
    //     point connects to the start/end points.
    return (
      <svg
        ref={node => (this.node = node)}
        viewBox={\`0 0 \${viewBoxWidth} \${viewBoxHeight}\`}
        onMouseMove={ev => this.handleMouseMove(ev)}
        onMouseUp={() => this.handleMouseUp()}
        onMouseLeave={() => this.handleMouseUp()}
        style={{ width: '100%', overflow: 'visible' }}
      >
        <ConnectingLine
          from={startPoint}
          to={controlPoint}
        />
        <ConnectingLine from={controlPoint} to={endPoint} />

        <Curve instructions={instructions} />

        <LargeHandle
          coordinates={startPoint}
          onMouseDown={() =>
            this.handleMouseDown('startPoint')
          }
        />

        <LargeHandle
          coordinates={endPoint}
          onMouseDown={() =>
            this.handleMouseDown('endPoint')
          }
        />

        <SmallHandle
          coordinates={controlPoint}
          onMouseDown={() =>
            this.handleMouseDown('controlPoint')
          }
        />
      </svg>
    );
  }
}

// These helper stateless-functional-components allow us
// to reuse styles, and give each shape a meaningful name.

const ConnectingLine = ({ from, to }) => (
  <line
    x1={from.x}
    y1={from.y}
    x2={to.x}
    y2={to.y}
    stroke="rgb(200, 200, 200)"
    strokeDasharray="5,5"
    strokeWidth={2}
  />
);

const Curve = ({ instructions }) => (
  <path
    d={instructions}
    fill="none"
    stroke="rgb(213, 0, 249)"
    strokeWidth={5}
  />
);

const LargeHandle = ({ coordinates, onMouseDown }) => (
  <ellipse
    cx={coordinates.x}
    cy={coordinates.y}
    rx={15}
    ry={15}
    fill="rgb(244, 0, 137)"
    onMouseDown={onMouseDown}
    style={{ cursor: '-webkit-grab' }}
  />
);

const SmallHandle = ({ coordinates, onMouseDown }) => (
  <ellipse
    cx={coordinates.x}
    cy={coordinates.y}
    rx={8}
    ry={8}
    fill="rgb(255, 255, 255)"
    stroke="rgb(244, 0, 137)"
    strokeWidth={2}
    onMouseDown={onMouseDown}
    style={{ cursor: '-webkit-grab' }}
  />
);`;

const interpolateFunctionCode = `/**
* \`getInterpolatedValue\` provides a midpoint value
* between y1 and y2, based on the ratio provided.
*
* @param {number} y1 - the value when our curve is
*                      totally curvy
* @param {number} y2 - the value when our curve is
*                      totally flat
* @param {number} x  - a value from 0 to 1 that
*                      represents the ratio of curvy
*                      to flat (0 = totally curvy,
*                      1 = totally flat).
*/
const getInterpolatedValue = (y1, y2, x) => {
 // The slope of a line can be calculated as Δy / Δx.
 //
 // In this case, the domain of our function (AKA the
 // possible X values) are from 0 (x1) to 1 (x2).
 // Δx is therefore just equal to 1 (since 1 - 0 = 1).
 //
 // Because dividing by 1 has no effect, our slope in
 // this case can just be Δy.
 const a = y2 - y1;

 // Next, we know that y = ax + b.
 //
 // \`b\` is the Y-axis intercept, which we know is \`y1\`,
 // since \`y1\` is the \`y\` value when \`x\` is 0.
 return a * x + y1;
}


// Let's test it! Feel free to sub in your own values
// to see how the output changes.

// Case 1: \`y\` ranges from 200 to 0, with 25% through.
const case1 = getInterpolatedValue(200, 0, 0.25);

// Case 2: \`y\` ranges from 0-1000, and we're 90% there.
const case2 = getInterpolatedValue(0, 1000, 0.9);


render(
 <span style={{ fontSize: 21 }}>
   <h4 style={{ fontSize: 24 }}>Test cases:</h4>
   Case 1 is {case1}.<br />
   Case 2 is {case2}.
 </span>
);
`;

const reactScrollFlattenerCode = `class DynamicBezierCurve extends React.PureComponent {
  constructor(props) {
    super(props);

    // As the user scrolls through our scrollable area,
    // the scrollRatio represents the amount completed,
    // from 0 (way at the bottom) to 1 (at the top).
    this.state = {
      scrollRatio: 0,
    };

    // This live-editing environment doesn't support
    // property-initializer syntax, so I'm doing my
    // binds in the constructor. 🤷
    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll(ev) {
    const windowHeight = window.innerHeight;
    const svgBB = this.node.getBoundingClientRect();

    const pixelsScrolled = windowHeight - svgBB.top;

    let scrollRatio = pixelsScrolled / windowHeight;

    // We don't care about the negative values when it's
    // below the viewport, or the greater-than-1 values when
    // it's above the viewport.
    scrollRatio = clamp(scrollRatio, 0, 1);

    // Small optimization, avoid re-rendering when the
    // SVG isn't in the viewport.
    if (this.state.scrollRatio !== scrollRatio) {
      this.setState({ scrollRatio });
    }
  }

  render() {
    const { scrollRatio } = this.state;

    // Use our \`getInterpolatedValue\` function from the
    // previous code snippet to figure out the values for
    // the start point and the control points.
    const startPoint = getInterpolatedValue(
      300, // curvy value
      0, // flat value
      scrollRatio
    );

    const firstControlPoint = getInterpolatedValue(
      -100, // curvy value
      0, // flat value
      scrollRatio
    );

    const secondControlPoint = getInterpolatedValue(
      450, // curvy value
      0, // flat value
      scrollRatio
    );

    // Unlike the other 3 points, the \`endPoint\` is
    // constant, and doesn't need interpolation.
    const endPoint = 0;

    // Create the SVG path instructions, using our
    // interpolated values.
    // Unlike previous examples, we want to fill this one
    // in, not just make a stroked line. So we need to add
    // a couple other lines after the curve, to make sure
    // the box fills in correctly.
    const instructions = \`
      M 0,\${startPoint}
      C 100,\${firstControlPoint}
        200,\${secondControlPoint}
        300,\${endPoint}
      L 300,300
      L 0,300
    \`;

    // NOTE: the instructions created assume a 300x300
    // viewBox. To make this component more flexible, you
    // could set \`viewBoxWidth\` and \`viewBoxHeight\`
    // variables as props.

    return (
      <svg
        ref={node => (this.node = node)}
        viewBox="0 0 300 300"
      >
        <path d={instructions} fill="hotpink" />
      </svg>
    );
  }
}

// Utility function that clamps a given value to a
// specific range (inclusive, between min and max).
const clamp = (val, min, max) =>
  Math.max(min, Math.min(max, val));
`;

const reactScrollFlattenerRefactoredCode = `/**
* Our first component, \`ScrollArea\`, tracks its children
* as they're scrolled through the scrollable area (a
* specific subset of the viewport).
*/
const scrollAreaPropTypes = {
 // The number of pixels between the top of the viewport,
 // and the top of the scrollable area:
 topBuffer: PropTypes.number.isRequired,
 // The height, in pixels, of the scrollable area:
 areaHeight: PropTypes.number.isRequired,
 // We'll make the \`scrollRatio\` data available to its
 // children with a \`children\` render prop:
 children: PropTypes.func.isRequired,
};

class ScrollArea extends React.PureComponent {
 constructor(props) {
   super(props);

   this.state = {
     scrollRatio: 0,
   };

   this.handleScroll = this.handleScroll.bind(this);
 }

 componentDidMount() {
   window.addEventListener('scroll', this.handleScroll);
 }

 componentWillUnmount() {
   window.removeEventListener('scroll', this.handleScroll);
 }

 handleScroll(ev) {
   const { topBuffer, areaHeight } = this.props;

   const windowHeight = window.innerHeight;
   const boundingBox = this.node.getBoundingClientRect();

   const distanceToTop = boundingBox.top - topBuffer;
   const pixelsScrolled = areaHeight - distanceToTop;

   let scrollRatio = pixelsScrolled / areaHeight;
   scrollRatio = clamp(scrollRatio, 0, 1);

   if (this.state.scrollRatio !== scrollRatio) {
     this.setState({ scrollRatio });
   }
 }

 render() {
   return (
     <div ref={node => this.node = node}>
       {this.props.children(this.state)}
     </div>
   );
 }
}

ScrollArea.propTypes = scrollAreaPropTypes;

/**
* This is a simple Bézier curve presentational component.
*/
const BezierCurve = ({
 viewBoxWidth,
 viewBoxHeight,
 startPoint,
 firstControlPoint,
 secondControlPoint,
 endPoint,
 fill = 'hotpink'
}) => {
 return (
   <svg viewBox={\`0 0 \${viewBoxWidth} \${viewBoxHeight}\`}>
     <path
       d={\`
         M \${startPoint}
         C \${firstControlPoint}
           \${secondControlPoint}
           \${endPoint}
         L \${viewBoxWidth},\${viewBoxHeight}
         L 0,\${viewBoxHeight}
       \`}
       fill={fill}
     />
   </svg>
 );
}

/**
* The two components above are the building blocks
* for the effect we want to build. This last component
* assembles them.
*
* It takes the \`headerHeight\` as a prop, so that we
* can flatten it at the right moment.
*/
const ScrollBasedBezier = ({ headerHeight }) => (
 <ScrollArea
   topBuffer={headerHeight}
   areaHeight={
     // By setting a relatively small ScrollArea height,
     // we get to see the fully-curved version for a bit
     // before the flattening starts :D
     window.innerHeight * 0.5
   }
 >
   {({ scrollRatio }) => {
     // Hardcoding these values since this component
     // isn't meant to be reusable.
     const viewBoxWidth = 300;
     const viewBoxHeight = 300;

     const startPointY = getInterpolatedValue(
       300,
       0,
       scrollRatio
     );

     const firstControlPointY = getInterpolatedValue(
       -100,
       0,
       scrollRatio
     );

     const secondControlPointY = getInterpolatedValue(
       450,
       0,
       scrollRatio
     );

     const endPointY = 0;

     return (
       <BezierCurve
         viewBoxWidth={viewBoxWidth}
         viewBoxHeight={viewBoxHeight}
         startPoint={[0, startPointY]}
         firstControlPoint={[100, firstControlPointY]}
         secondControlPoint={[200, secondControlPointY]}
         endPoint={[300, endPointY]}
       />
     );
   }}
 </ScrollArea>
);

const clamp = (val, min, max) =>
 Math.max(min, Math.min(max, val));
`;
